import { Button, CircularProgress, Grid, Typography } from "@mui/material"
import { Box } from "@mui/material"
import { FormBuilder } from "@components/FormBuilder"
import { useEffect, useMemo, useState } from "react"
import { getContractFormFields } from "./const"
import { FormProvider, useForm } from "react-hook-form"
import {
  useLazyGetContractByKeyQuery,
  useUpdateContractMutation,
} from "@store/services/contractService"
import Notification from "@components/Notification"
import { useNavigate, useParams } from "react-router"
import { useOrgDivOptions } from "@hooks/useOrgDivOptions"
import { ContractDto } from "@custom-types/contract"
import { ProductFamilies, ProductFamily } from "@features/administration/contracts/ProductFamilies"
import { EditContractFormApi } from "@features/administration/contracts/EditContract/types"

export const EditContract = () => {
  const formMethods = useForm<EditContractFormApi>({
    defaultValues: {
      organisation_key: "",
      division_key: "",
      salesforce_id: "",
      salesforce_contact: "",
      salesforce_contact_email: "",
      active: true,
      description: "",
      sftp: "",
      start_date: new Date(),
      end_date: new Date(),
      product_families: [],
      webhook_url: "",
    },
  })
  const watchedOrganisationKey = formMethods.watch("organisation_key")
  const navigate = useNavigate()
  const [updateContract, { data: updatedContract }] = useUpdateContractMutation()
  const [fetchContract, { data: contractData, isFetching: isContractDataFetching }] =
    useLazyGetContractByKeyQuery()
  const { organisationOptions, divisionOptions } = useOrgDivOptions(watchedOrganisationKey)
  const [submitTimeout, setSubmitTimeout] = useState(-1)
  const [updateData, setUpdateData] = useState<EditContractFormApi | undefined>(undefined)
  const { id } = useParams()

  const key = useMemo(() => id?.replaceAll("%23", "#") ?? "", [id])

  const onSubmit = (data: EditContractFormApi) => {
    // delay submit so onUpdateProductFamilies can have chance to trigger
    // prevents double updateContract and last_updated conflict error message from backend
    clearTimeout(submitTimeout)
    setSubmitTimeout(window.setTimeout(() => setUpdateData(data), 500))
  }

  useEffect(() => {
    if (updateData) {
      updateContract({ key: encodeURIComponent(key), data: updateData })
      setUpdateData(undefined)
    }
  }, [key, updateContract, updateData])

  const contractFormFields = useMemo(
    () => getContractFormFields({ divisionOptions, organisationOptions }),
    [divisionOptions, organisationOptions],
  )

  const onCancelClick = () => navigate("/Administration/Contracts")

  const onUpdateProductFamilies = (value: ProductFamily[]) => {
    formMethods.setValue("product_families", value)
    formMethods.handleSubmit(onSubmit)()
  }

  useEffect(() => {
    if (key) {
      fetchContract(encodeURIComponent(key))
    }
  }, [key, fetchContract])

  useEffect(() => {
    if (!contractData) return

    const defaultValues: ContractDto = {
      ...contractData,
      start_date: new Date(contractData.start_date).toISOString(),
      end_date: new Date(contractData.end_date).toISOString(),
    }
    formMethods.reset(defaultValues)
  }, [contractData, formMethods])

  return (
    <Box sx={{ mt: 4, px: 1.5 }}>
      <Typography variant="h4" sx={{ mb: 4 }}>
        Edit Contract
      </Typography>
      {isContractDataFetching ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <FormProvider {...formMethods}>
          <form autoComplete={"off"} onSubmit={formMethods.handleSubmit(onSubmit)}>
            <Grid container={true} spacing={2} alignItems="center" sx={{ mb: 4 }}>
              {Object.entries(contractFormFields).map(([fieldName, component]) => (
                <Grid item xs={6} key={fieldName}>
                  <FormBuilder
                    field={fieldName}
                    component={component}
                    helperText={
                      formMethods.formState.errors[fieldName as keyof EditContractFormApi]?.message
                    }
                    inputWidth="100%"
                    dropdownWidth="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Box sx={{ mt: 2, mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Graphs
              </Typography>
              <ProductFamilies
                initialData={contractData?.product_families || []}
                updateCallback={onUpdateProductFamilies}
              />
            </Box>
            <Grid item={true} xs={12}>
              <Button variant="contained" type="submit">
                Update
              </Button>
              <Button
                sx={{ ml: 1 }}
                variant="contained"
                color={"error"}
                type="button"
                onClick={onCancelClick}
              >
                Cancel
              </Button>
            </Grid>
          </form>
        </FormProvider>
      )}
      <Notification
        message={updatedContract ? "Contract has updated successfully" : ""}
        onClose={() => navigate("/Administration/Contracts")}
      />
    </Box>
  )
}
